import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { Button } from 'antd';
import moment from 'moment';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { toolCheckoutsRestCrudThunksForSupplier } from '../../thunks/tool_checkouts_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import { EmptyState } from '../empty_state/EmptyState';
import DropOffModal from '../tools_index_page/DropOffModal';
import DropOffConfirmationModal from '../tools_index_page/DropOffConfirmationModal';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';

const MY_PICKUPS_IN_USE_TARGET_COLLECTION = 'myPickupsInUseIndex';

const ToolsMyPickupsInUsePage: FC<any> = ({
	toolCheckouts,
	fetchCheckouts,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const [dropOffTool, setDropOffTool] = useState(null);
	const [dropOffConfirmationType, setDropOffConfirmationType] = useState(null);

	const handleDropOff = useCallback(
		(checkout) => {
			const toolData = nullSafeGet('tool', checkout);
			const toolWithCheckout = {
				...toolData,
				currentCheckout: checkout,
			};

			const pickedUpBy = nullSafeGet('pickedUpBy', checkout);
			const pickedUpFor = nullSafeGet('pickedUpFor', checkout);
			const currentEmail = nullSafeGet('email', currentUser);

			if (pickedUpBy === currentEmail && pickedUpFor === currentEmail) {
				setDropOffTool(toolWithCheckout);
			} else if (pickedUpBy !== currentEmail) {
				setDropOffTool(toolWithCheckout);
				setDropOffConfirmationType('someoneElse');
			} else if (pickedUpBy === currentEmail && pickedUpFor !== currentEmail) {
				setDropOffTool(toolWithCheckout);
				setDropOffConfirmationType('youForSomeoneElse');
			}
		},
		[currentUser]
	);

	const refreshCheckouts = useCallback(() => {
		const pagination = nullSafeGetOrElse(
			`${MY_PICKUPS_IN_USE_TARGET_COLLECTION}.pagination`,
			toolCheckouts,
			{}
		);
		const sorting = nullSafeGetOrElse(
			`${MY_PICKUPS_IN_USE_TARGET_COLLECTION}.sorting`,
			toolCheckouts,
			{}
		);
		const filters = nullSafeGetOrElse(
			`${MY_PICKUPS_IN_USE_TARGET_COLLECTION}.filters`,
			toolCheckouts,
			{}
		);
		fetchCheckouts(null, MY_PICKUPS_IN_USE_TARGET_COLLECTION, pagination, sorting, filters);
	}, [toolCheckouts, fetchCheckouts]);

	const getDropOffConfirmationContactName = useCallback(() => {
		if (!dropOffTool) return '';
		return getContactName(nullSafeGet('currentCheckout.pickedUpForContact', dropOffTool)).trim();
	}, [dropOffTool]);

	const initialFilters = useMemo(() => ({ pickedUpForMe: true, status: 'open' }), []);

	const columns = useMemo(
		() => [
			{
				title: 'Tool',
				dataIndex: 'tool',
				render: (text, record) => (
					<span className="bodymedium">{nullSafeGet('tool.name', record) || '--'}</span>
				),
			},
			{
				title: 'Pickup Date',
				dataIndex: 'pickedUpAt',
				render: (text, record) => (
					<span className="bodyregular">
						{record.pickedUpAt ? moment(record.pickedUpAt).format('D MMM, ha') : '--'}
					</span>
				),
			},
			{
				title: 'Location',
				dataIndex: 'stockLocation',
				render: (text, record) => (
					<span className="bodyregular">
						{nullSafeGetOrElse('tool.stockLocation.name', record, '--')}
					</span>
				),
			},
			{
				title: 'Picked Up By',
				dataIndex: 'pickedUpBy',
				render: (text, record) => {
					const pickedUpBy = nullSafeGet('pickedUpBy', record);
					const pickedUpFor = nullSafeGet('pickedUpFor', record);
					if (pickedUpBy === pickedUpFor) {
						return <span className="bodyregular">Self</span>;
					}
					const pickedUpByName =
						getContactName(nullSafeGet('pickedUpByContact', record)).trim() || '--';
					return <span className="bodyregular">Pickup: {pickedUpByName} / Dropoff: Self</span>;
				},
			},
			{
				title: 'Work Orders',
				dataIndex: 'workOrders',
				render: (text, record) => {
					const workOrders = nullSafeGetOrElse('workOrders', record, []);
					if (!workOrders || workOrders.length === 0) {
						return <span className="bodyregular">--</span>;
					}
					return (
						<div className="flex flex-row flex-wrap gap-x-3 gap-y-1">
							{workOrders.map((workOrder) => (
								<HyperLink
									key={workOrder.id}
									entityType={ENTITY_TYPE.WORK_ORDER}
									entityId={workOrder.id}
									text={`WO #${workOrder.id}`}
								/>
							))}
						</div>
					);
				},
			},
			{
				title: 'Action',
				dataIndex: 'action',
				render: (text, record) => (
					<Button
						type="primary"
						onClick={(event) => {
							event.stopPropagation();
							handleDropOff(record);
						}}
					>
						Drop Off
					</Button>
				),
			},
		],
		[handleDropOff]
	);

	return (
		<>
			<ReduxDataTable
				entityCollectionName="tool_checkouts"
				targetCollectionName={MY_PICKUPS_IN_USE_TARGET_COLLECTION}
				fetchData={toolCheckoutsRestCrudThunksForSupplier}
				stateSlice={toolCheckouts}
				tableColumns={columns}
				filterConfig={[]}
				keyAccessor={(el) => el.id}
				showHeader={false}
				hideFilters
				hideSearch
				initialFilters={initialFilters}
				emptyState={
					<EmptyState
						headline="No tools in use"
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									You have no tools currently picked up.
								</div>
							</div>
						}
					/>
				}
			/>
			{dropOffConfirmationType && dropOffTool && (
				<DropOffConfirmationModal
					visible
					type={dropOffConfirmationType}
					contactName={getDropOffConfirmationContactName()}
					onCancel={() => {
						setDropOffConfirmationType(null);
						setDropOffTool(null);
					}}
					onConfirm={() => setDropOffConfirmationType(null)}
				/>
			)}
			{dropOffTool && !dropOffConfirmationType && (
				<DropOffModal
					visible
					tool={dropOffTool}
					onCancel={() => setDropOffTool(null)}
					onSuccess={() => {
						setDropOffTool(null);
						refreshCheckouts();
					}}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	toolCheckouts: state.tool_checkouts,
});

const mapDispatchToProps = (dispatch) => ({
	fetchCheckouts: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			toolCheckoutsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ToolsMyPickupsInUsePage)
);
